import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AssemblyDayLogInput,
  ItemResponse,
  PortalDayLogsResponse,
  PortalDayWorkerInput,
  SignAssemblyDayInput,
  UpdateAssemblyDayLogInput,
  UpdatePortalDayWorkerInput,
} from '@proerp/shared';
import { portalRequest } from './apiClient';

export const PORTAL_DAY_LOGS_KEY = ['portal', 'day-logs'] as const;

/**
 * Bau-Tagesberichte eines Einsatzes (ADR 0074 + ADR 0087). Wie die Unterlagen
 * erst beim Aufklappen geladen — die Einsatzliste soll nicht für jeden Einsatz
 * eine Abfrage stellen, die niemand liest.
 */
export function usePortalDayLogs(
  assemblyTaskId: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<PortalDayLogsResponse> {
  return useQuery({
    queryKey: [...PORTAL_DAY_LOGS_KEY, assemblyTaskId],
    queryFn: async () => {
      const response = await portalRequest<ItemResponse<PortalDayLogsResponse>>(
        `/assembly/${assemblyTaskId}/day-logs`,
      );
      return response.data;
    },
    enabled: options.enabled ?? true,
    retry: false,
  });
}

/**
 * Jede Schreib-Operation setzt die Antwort direkt in den Cache statt nachzuladen:
 * Auf der Baustelle ist die Verbindung schlecht, und ein zweiter Umlauf nur, um
 * dasselbe zu erfahren, kostet dort spürbar Zeit.
 */
function useDayMutation<TInput>(
  assemblyTaskId: string,
  request: (input: TInput) => Promise<ItemResponse<PortalDayLogsResponse>>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => request(input).then((response) => response.data),
    onSuccess: (data) =>
      queryClient.setQueryData([...PORTAL_DAY_LOGS_KEY, assemblyTaskId], data),
  });
}

export function useAddPortalDay(assemblyTaskId: string) {
  return useDayMutation<AssemblyDayLogInput>(assemblyTaskId, (input) =>
    portalRequest(`/assembly/${assemblyTaskId}/day-logs`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export function useUpdatePortalDay(assemblyTaskId: string) {
  return useDayMutation<{ dayId: string; input: UpdateAssemblyDayLogInput }>(
    assemblyTaskId,
    ({ dayId, input }) =>
      portalRequest(`/assembly/${assemblyTaskId}/day-logs/${dayId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
  );
}

/**
 * Personal am Tag (wer montiert hat). Drei Haken statt einer Sammel-Mutation:
 * Anlegen, Ändern und Entfernen sind auf der Baustelle drei verschiedene
 * Handgriffe, und jeder soll für sich scheitern dürfen.
 */
export function useAddPortalDayWorker(assemblyTaskId: string) {
  return useDayMutation<{ dayId: string; input: PortalDayWorkerInput }>(
    assemblyTaskId,
    ({ dayId, input }) =>
      portalRequest(`/assembly/${assemblyTaskId}/day-logs/${dayId}/workers`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  );
}

export function useUpdatePortalDayWorker(assemblyTaskId: string) {
  return useDayMutation<{ dayId: string; workerId: string; input: UpdatePortalDayWorkerInput }>(
    assemblyTaskId,
    ({ dayId, workerId, input }) =>
      portalRequest(`/assembly/${assemblyTaskId}/day-logs/${dayId}/workers/${workerId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
  );
}

export function useRemovePortalDayWorker(assemblyTaskId: string) {
  return useDayMutation<{ dayId: string; workerId: string }>(
    assemblyTaskId,
    ({ dayId, workerId }) =>
      portalRequest(`/assembly/${assemblyTaskId}/day-logs/${dayId}/workers/${workerId}`, {
        method: 'DELETE',
      }),
  );
}

export function useSignPortalDay(assemblyTaskId: string) {
  return useDayMutation<{ dayId: string; input: SignAssemblyDayInput }>(
    assemblyTaskId,
    ({ dayId, input }) =>
      portalRequest(`/assembly/${assemblyTaskId}/day-logs/${dayId}/signatures`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  );
}
